import React, { useState } from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import Heading from '@atlaskit/heading';
import { AvatarPickerDialog } from '@atlaskit/media-avatar-picker';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import type { IntlShapeV2 as IntlShape } from '@atlassian/jira-intl/src/v2/types.tsx';
import {
	ContextualAnalyticsData,
	SCREEN,
	fireUIAnalytics,
	fireTrackAnalytics,
	AnalyticsEventToProps,
} from '@atlassian/jira-product-analytics-bridge';
import { portalIconUpdateExperience } from '../../../experiences';
import { saveLogo, deleteLogo } from '../../../services';
import messages from './messages';

type Props = {
	portalLogoUrl: string;
	baseUrl: string;
	projectKey: string;
	projectId: number;
	intl: IntlShape;
};

export const AvatarWithAnalytics = AnalyticsEventToProps('customerPortalLogoPicker', {
	onImagePicked: 'uploaded',
})(AvatarPickerDialog);

export const IconUpload = (props: Props) => {
	const {
		portalLogoUrl,
		baseUrl,
		projectKey,
		projectId,
		intl: { formatMessage },
	} = props;

	const [isUploaderOpen, setIsUploaderOpen] = useState(false);
	const [logoUrlState, setLogoUrlState] = useState(portalLogoUrl);
	const [isLogoLoading, setIsLogoLoading] = useState(false);
	const [hasCustomIcon, setHasCustomIcon] = useState(portalLogoUrl !== '');

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const setNewLogo = async (file: string, analyticsEvent: any) => {
		setIsLogoLoading(true);
		const prevHasCustomIcon = hasCustomIcon;
		setHasCustomIcon(true);
		setIsUploaderOpen(false);
		try {
			const tempURL = await saveLogo({ baseUrl, projectKey, file });
			setLogoUrlState(tempURL);
			fireTrackAnalytics(analyticsEvent, 'portalLogoUpload submitted', { projectId });
			portalIconUpdateExperience.success();
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			setHasCustomIcon(prevHasCustomIcon);
			fireTrackAnalytics(analyticsEvent, 'portalLogoUpload failed', {
				error: error.toString(),
			});
			portalIconUpdateExperience.failure();
		}
		setIsLogoLoading(false);
	};

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const setDeleteLogo = async (analyticsEvent: any) => {
		setHasCustomIcon(false);
		setIsLogoLoading(false);
		try {
			await deleteLogo({ baseUrl, projectKey });
			fireTrackAnalytics(analyticsEvent, 'portalLogoDelete submitted');
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			setHasCustomIcon(true);
			fireTrackAnalytics(analyticsEvent, 'portalLogoDelete failed', {
				error: error.toString(),
			});
		}
	};

	return (
		<>
			<ContextualAnalyticsData sourceName="viewCustomerPortalSettings" sourceType={SCREEN}>
				<HeadingContainer>
					<Heading level="h200">{formatMessage(messages.logo)}</Heading>
				</HeadingContainer>
				<ButtonContainer>
					{hasCustomIcon && !isLogoLoading && (
						<LogoImage src={logoUrlState} alt={formatMessage(messages.portalLogoAlt)} />
					)}
					{isLogoLoading && (
						<SpinnerContainer>
							<Spinner size="medium" />
						</SpinnerContainer>
					)}
					<Button
						appearance="default"
						spacing="default"
						onClick={(event, analyticsEvent) => {
							setIsUploaderOpen(true);
							fireUIAnalytics(analyticsEvent, 'CustomerPortalLogoUploaderOpened');
						}}
					>
						{hasCustomIcon
							? formatMessage(messages.uploadButtonChange)
							: formatMessage(messages.uploadButtonAdd)}
					</Button>
					{hasCustomIcon && (
						<DeleteButton
							appearance="subtle"
							spacing="default"
							testId="servicedesk-settings-customer-portal-v2.ui.customer-portal-forms.icon-upload.delete-button"
							onClick={(event, analyticsEvent) => {
								setDeleteLogo(analyticsEvent);
								fireUIAnalytics(analyticsEvent, 'CustomerPortalLogoDeletePressed');
							}}
						>
							{formatMessage(messages.deleteButton)}
						</DeleteButton>
					)}
				</ButtonContainer>
				{isUploaderOpen && (
					<AvatarWithAnalytics
						// @ts-expect-error - TS7006 - Parameter 'file' implicitly has an 'any' type. | TS7006 - Parameter '_' implicitly has an 'any' type. | TS7006 - Parameter 'analyticsEvent' implicitly has an 'any' type.
						onImagePicked={(file, _, analyticsEvent) => {
							portalIconUpdateExperience.start();
							fireUIAnalytics(analyticsEvent, 'CustomerPortalLogoUploadPressed');
							setNewLogo(file, analyticsEvent);
						}}
						onCancel={() => {
							setIsUploaderOpen(false);
						}}
						title={formatMessage(messages.title)}
						primaryButtonText={formatMessage(messages.upload)}
					/>
				)}
			</ContextualAnalyticsData>
		</>
	);
};

export default injectIntl(IconUpload);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ButtonContainer = styled.div({
	paddingTop: token('space.200', '16px'),
	verticalAlign: 'middle',
	display: 'flex',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DeleteButton = styled(Button)({
	marginLeft: token('space.100', '8px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SpinnerContainer = styled.div({
	width: '50px',
	height: '50px',
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	marginRight: token('space.200', '16px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LogoImage = styled.img({
	height: '50px',
	width: '50px',
	marginRight: token('space.200', '16px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeadingContainer = styled.div({
	marginTop: token('space.250', '16px'),
});
